/**
 * The asset proxy contract of the public developer APIs. A response references
 * every upstream-hosted image through `/api/assets?u=<base64url>` on the
 * developers app, so no upstream host appears in a payload, and that route
 * redirects only to a URL this module admits: an `https` URL on the ticketing
 * CDN, or in the event or others image folder of a ticketing storage bucket.
 * The allow-list is the whole security boundary of the redirect, so it stays
 * exact.
 */

const TICKETING_HOST_PATTERN = /(^|\.)tixologi\.com$/i;
const TICKETING_BUCKET_HOST = "storage.googleapis.com";
// The events and others folders of both buckets in every environment: the images are public, so the test bucket opens nothing.
const TICKETING_IMAGE_PREFIXES = [
  "/partner-portal-storage/ticketing/events/",
  "/partner-portal-storage/ticketing/others/",
  "/partner-test-storage/ticketing/events/",
  "/partner-test-storage/ticketing/others/",
];

function isAllowedAssetUrl(target: URL): boolean {
  if (target.protocol !== "https:") {
    return false;
  }
  if (TICKETING_HOST_PATTERN.test(target.hostname)) {
    return true;
  }
  return (
    target.hostname === TICKETING_BUCKET_HOST &&
    TICKETING_IMAGE_PREFIXES.some((prefix) =>
      target.pathname.startsWith(prefix)
    )
  );
}

function parseAllowedAssetUrl(raw: string): URL | null {
  let target: URL;
  try {
    target = new URL(raw);
  } catch {
    return null;
  }
  return isAllowedAssetUrl(target) ? target : null;
}

/**
 * Rewrites an allow-listed asset URL to the developers-app asset redirect.
 * Any other URL becomes null, so no upstream host reaches a payload.
 */
export function rewriteAssetUrl(
  url: string | null,
  assetBaseUrl: string
): string | null {
  if (!url || !parseAllowedAssetUrl(url)) {
    return null;
  }
  const encoded = Buffer.from(url, "utf8").toString("base64url");
  return `${assetBaseUrl}/api/assets?u=${encoded}`;
}

/** The upstream URL behind a `u` param, or null unless it decodes to an allow-listed URL. */
export function decodeAssetProxyParam(encoded: string | null): URL | null {
  if (!encoded) {
    return null;
  }
  return parseAllowedAssetUrl(
    Buffer.from(encoded, "base64url").toString("utf8")
  );
}
